/* eslint-disable max-len, arrow-body-style, no-mixed-operators */
import { mapActions, mapGetters, mapState } from 'vuex';
import Contract from '@/models/contract';
import marketChangePrice from '@/components/market/market-change-price/market-change-price.vue';
import marketSharesBar from '@/components/market/market-shares-bar/market-shares-bar.vue';
import buttonPrice from '@/components/market/v2/button-price/button-price.vue';
import { contractOwnershipNum, isMarketSingle, isMarketSettled } from '@/utils/helpers';
import constants from '@/constants';
import { waitForFirebaseInitialized } from '@/utils/mutex';
import { MODALS } from '@/components/app/app-modals/app-modals';
import accountVerifyMixin from '@/mixins/accountVerify';

const thousandDisplay = (value) => {
  if (value >= 1.0e+4) {
    return `${Math.round(value / 1.0e+3)}K`;
  }
  return (value >= 1.0e+3) ? `${Math.floor((10 * value) / 1.0e+3) / 10}K` : value;
};

const TITLE_LATEST_PRICE = 'Latest Price';
const OPERATIONS = {
  BUY: 'buy',
  SELL: 'sell',
};
const VALUES = {
  YES: 'yes',
  NO: 'no',
};
const BUTTONS = {
  BUY_NO: {
    id: 'buy-no',
    type: OPERATIONS.BUY,
    value: VALUES.NO,
  },
  BUY_NO_ALT: {
    id: 'buy-no-alt',
    type: OPERATIONS.BUY,
    value: VALUES.NO,
  },
  BUY_YES: {
    id: 'buy-yes',
    type: OPERATIONS.BUY,
    value: VALUES.YES,
  },
  SELL_NO: {
    id: 'sell-no',
    type: OPERATIONS.SELL,
    value: VALUES.NO,
  },
  SELL_YES: {
    id: 'sell-yes',
    type: OPERATIONS.SELL,
    value: VALUES.YES,
  },
};

export function getActiveButtonAfterSwitch(contractModel, switchTo) {
  let button = BUTTONS.BUY_NO_ALT;

  if (switchTo === 'right') {
    if (contractModel.mode === VALUES.NO) {
      button = BUTTONS.SELL_NO;
    } else if (contractModel.mode === VALUES.YES) {
      button = BUTTONS.SELL_YES;
    }
  } else if (switchTo === 'left') {
    if (contractModel.mode === VALUES.NO) {
      button = BUTTONS.BUY_NO;
    } else {
      button = BUTTONS.BUY_YES;
    }
  }

  return button;
}

export default {
  mixins: [
    accountVerifyMixin,
  ],
  components: {
    buttonPrice,
    marketChangePrice,
    marketSharesBar,
  },
  props: {
    market: {
      type: Object,
      required: true,
    },
    contract: {
      type: Object,
      required: true,
    },
    layout: {
      type: String,
      default: 'default',
    },
    showPurchaseOnInit: {
      type: Boolean,
      default: false,
    },
    forCmsWidget: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      BUTTONS,
      OPERATIONS,
      VALUES,
      isWaiting: false,
      activeButton: null,
    };
  },
  created() {
    this.activatePurchaseJourneyForMarket();
  },
  computed: {
    ...mapGetters('user', ['isLoggedIn']),
    ...mapGetters('app', ['isLayoutMobile', 'isLayoutDesktop']),
    ...mapState('trade', ['tradeOptions', 'tradeOperationToggle']),
    ...mapState('user', ['status']),
    contractModel() {
      return new Contract(this.contract);
    },
    isMarketSingle() {
      return isMarketSingle(this.market);
    },
    singleMarketTitle() {
      return this.isMarketSingle ?
        TITLE_LATEST_PRICE :
        (this.contractModel.contractName || this.contractModel.name);
    },
    title() {
      return this.isMarketSingle ?
        this.singleMarketTitle :
        (this.contractModel.contractName || this.contractModel.name);
    },
    classModifiers() {
      return {
        'market-contract-horizontal-v2': true,
        'market-contract-horizontal-v2--no-shares-or-offers': !this.showOffersOrShares,
        [`market-contract-horizontal-v2--${this.layout}`]: this.layout,
      };
    },
    rowModifiers() {
      return {
        row: true,
        'row-y': this.isLayoutDesktop,
        'row-large-100': true,
      };
    },
    titleModifiers() {
      return {
        'market-contract-horizontal-v2__title': true,
        'market-contract-horizontal-v2__title--no-shares': !this.showSharesBar,
        'market-contract-horizontal-v2__title--no-offers': !this.showOpenOffers,
      };
    },
    classModifiersMobileHeaderTitle() {
      return {
        'market-contract-horizontal-v2__mobile-header-title': true,
        'market-contract-horizontal-v2__mobile-header-title--single': this.isMarketSingle,
      };
    },
    isLayoutDashboard() {
      return this.layout === 'dashboard';
    },
    infoPartModifiers() {
      return {
        column: true,
        'small-12': true,
        'column-large-100': true,
        'column-valign': true,
        'market-contract--dashboard': this.isLayoutDashboard,
        'market-contract--infopart': true,
      };
    },
    pricePartModifiers() {
      return {
        column: true,
        'small-12': true,
        'column-large-100': true,
        'column-valign': true,
        'medium-5': !this.isLayoutDashboard,
        'medium-3': this.isLayoutDashboard,
      };
    },
    imageAltText() {
      return this.contractModel.contractName;
    },
    imageUrl() {
      return this.contractModel.contractImageSmallUrl || this.contractModel.contractImageUrl;
    },
    contractImageStyleModifiers() {
      return {
        'background-image': `url(${this.imageUrl})`,
      };
    },
    isMarketDisabled() {
      const statuses = [
        constants.MARKET_EXPIRED,
        constants.MARKET_SUSPENDED,
        constants.MARKET_SETTLED,
        constants.MARKET_MAINTENANCE,
      ];
      return this.market && statuses.includes(this.market.status);
    },
    isMarketSettled() {
      return isMarketSettled(this.market);
    },
    openOffersText() {
      const buyOffers = thousandDisplay(this.contractModel.userOpenOrdersBuyQuantity, false);
      const sellOffers = thousandDisplay(this.contractModel.userOpenOrdersSellQuantity, false);
      // const prefix = (this.isLayoutDesktop && !this.isLayoutDashboard) ? 'Offers: ' : '';
      return `${buyOffers}/${sellOffers}`;
    },
    showOpenOffers() {
      return !this.isMarketSettled && this.contractModel.userOpenOrders > 0;
    },
    showSharesBar() {
      return !this.isMarketSettled && this.contractModel.userQuantity > 0;
    },
    showOffersOrShares() {
      return this.showSharesBar || this.showOpenOffers;
    },
    showOuterPriceButton() {
      return !this.isLayoutDashboard && this.isLayoutDesktop;
    },
    showInnerPriceButton() {
      return this.isLayoutDashboard || this.isLayoutMobile;
    },
    disabledMarketButtonText() {
      return this.market.status;
    },
    shouldClosePurchaseJourney() {
      return this.isLayoutDesktop &&
        this.tradeOptions &&
        this.tradeOptions.contractId === this.contract.contractId;
    },
    currentPrice() {
      return this.contractModel.lastTradePriceAsCents;
    },
  },
  watch: {
    // opposite side of order book was clicked -- switching sides
    tradeOperationToggle(switchTo) {
      if (this.contract.contractId !== this.tradeOptions.contractId || !switchTo) return;
      const button = getActiveButtonAfterSwitch(this.contractModel, switchTo);
      this.toggleTradeOperation(null);
      this.openPurchaseJourney(this.contractModel.contractId, button.type, button.value, null, button.id);
    },
  },
  methods: {
    ...mapActions('trade', ['setTradeOptions', 'toggleTradeOperation']),
    ...mapActions('popup', ['setCurrentPopup']),
    findActiveButton() {
      return Object.values(BUTTONS).find(i => i.id === this.activeButton);
    },
    getTypeAndValueIfButtonActiveOrDefaults() {
      let type = this.tradeOptions.operationType;
      let value = this.tradeOptions.operationValue;
      if (this.activeButton) {
        const button = this.findActiveButton();
        type = button.type;
        value = button.value;
      }
      return { type, value };
    },
    onClickSharesBar() {
      const { type, value } = this.getTypeAndValueIfButtonActiveOrDefaults();
      this.openPurchaseJourney(
        this.contractModel.contractId,
        type,
        value,
        'my-shares');
    },
    onClickOpenOffers() {
      let tab = 'sell-offers';
      const operationType = this.tradeOptions.operationType || '';
      if (
        (operationType === '' && this.contractModel.userOpenOrdersBuyQuantity) ||
        (operationType === OPERATIONS.BUY && this.contractModel.userOpenOrdersBuyQuantity)
      ) {
        tab = 'buy-offers';
      }
      const { type, value } = this.getTypeAndValueIfButtonActiveOrDefaults();
      this.openPurchaseJourney(
        this.contractModel.contractId,
        type,
        value,
        tab);
    },
    activatePurchaseJourneyForMarket() {
      const button = (this.contractModel.mode === VALUES.NO) ? BUTTONS.BUY_NO : BUTTONS.BUY_YES;
      this.activeButton = button.id;
      if (this.showPurchaseOnInit) {
        this.$emit('purchaseShown');
        this.openPurchaseJourney(
          this.contractModel.contractId, button.type, button.value, null, null);
      } else if (
        (!this.isMarketSingle || this.isLayoutMobile) &&
        this.tradeOptions &&
        (this.contract.contractId === this.tradeOptions.contractId) &&
        (
          button.type !== this.tradeOptions.operationType ||
          button.value !== this.tradeOptions.operationValue
        )
      ) {
        this.openPurchaseJourney(
          this.contractModel.contractId, button.type, button.value, null, null);
      }
    },
    onContractClick() {
      if (this.shouldClosePurchaseJourney) {
        this.setTradeOptions(null);
        return;
      }
      if (!this.isMarketDisabled && this.activeButton) {
        const $el = this.$refs[this.activeButton];
        if (!$el) return;
        const ev = new MouseEvent('click');
        $el.dispatchEvent(ev);
      } else {
        const button = (this.contractModel.mode === VALUES.NO) ? BUTTONS.BUY_NO : BUTTONS.BUY_YES;
        this.openPurchaseJourney(
          this.contractModel.contractId, button.type, button.value, null, null);
      }
    },
    isButtonActive(index) {
      const isOn = this.activeButton === index;
      return {
        'market-contract-horizontal-v2__button-single--on': isOn,
        'market-contract-horizontal-v2__button-single--off': !isOn,
      };
    },
    openPurchaseJourney(
      contractId, operationType, operationValue,
      defaultTab = null, activateButton = null,
    ) {
      if (this.isWaiting) {
        return;
      }

      this.isWaiting = true;
      waitForFirebaseInitialized()
        .then(() => {
          this.isWaiting = false;
          // contract does not modify tradeOptions, is simply used as a widget somewhere
          if (this.forCmsWidget) {
            this.$emit('contractClicked', { contractId });
            return;
          }
          if (this.isLoggedIn && this.status.isClassicEnabled === false) {
            this.setCurrentPopup(MODALS.ACCOUNT_ENABLE_CLASSIC);
            return;
          }
          if (this.isLoggedIn && !this.isUserVerified) {
            this.setCurrentPopup(MODALS.ACCOUNT_VERIFY);
            return;
          }
          if (this.isLoggedIn && !this.status.hasBeenFunded) {
            this.setCurrentPopup(MODALS.ADD_FUNDS);
            return;
          }
          if (this.isMarketSettled) return;
          if (this.isMarketDisabled && contractOwnershipNum(this.contractModel) === 0) return;
          if (activateButton) {
            this.activeButton = activateButton;
          }

          this.setTradeOptions({
            contractId,
            operationType,
            defaultTab,
            operationValue: operationValue !== null ?
              operationValue :
              (this.contractModel.mode || VALUES.YES),
          });
        });
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/market/v2/market-contract-horizontal-v2/market-contract-horizontal-v2.js